import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {DealerSearchData, Position} from "../lib/dealer-service";
import DealerService from "../lib/dealer-service";
import {useMap} from "@vis.gl/react-google-maps";

// Search radius for dealer
const RANGE_KM = 100;


interface SearchOption{
    type:string,
    query:string
}

interface GeolocationResult{
    coords:Position,
    type:string,
    query:string
}

interface StoreLocatorContext {
    position: Position | null,
    searchPosition:Position | null,
    dealers: DealerSearchData[] | null,
    zoom:number,
    hideResult:boolean,
    searching:boolean,
    selectedDealer:DealerSearchData |null,
    setZoom:(zoom:number)=>void,
    setPosition:(pos:Position)=>void,
    search:(result:GeolocationResult)=>void,
    selectDealer:(d:DealerSearchData | null)=>void,
    toggleHideResult:()=>void,
}


// Create a new context
const StoreLocatorContext = createContext<StoreLocatorContext>({
    position: null,
    searchPosition: null,
    searching:false,
    dealers: null,
    hideResult:false,
    selectedDealer:null,
    zoom:10,
    search:()=>{},
    setZoom:()=>{},
    setPosition:()=>{},
    selectDealer:()=>{},
    toggleHideResult:()=>{}
});


/**
 * Retrieve the correct Map zoom level for show all marker in map
 * @param map
 * @param dealers
 */
 const findZoomLevel=(map: google.maps.Map, dealers:DealerSearchData[]) => {
    if (!Array.isArray(dealers) || dealers.length === 0) {
        console.error("Invalid coordinates array.");
        return;
    }
    const bounds = new google.maps.LatLngBounds();
    dealers.forEach(d => {
        if (d.lat && d.lng) {
            bounds.extend(new google.maps.LatLng(parseFloat(d.lat), parseFloat(d.lng)));
        }
    });
    map.fitBounds(bounds);
    return map.getZoom();
}


const StoreLocatorProvider: React.FC<{ children: ReactNode }> = ({children}) => {

    const [position,setPosition] = useState<Position|null>(null);
    const [searchPosition,setSearchPosition] = useState<Position|null>(null);
    const [searchOption, setSearchOption] = useState<SearchOption>({
        type:'address',
        query:''
    });
    const [dealers,setDealers] = useState<DealerSearchData[]|null>(null);
    const [searching,setSearching] = useState(false);
    const [selectedDealer,setSelectedDealer] = useState<DealerSearchData|null>(null);
    const [hideResult, setHideResult] = useState<boolean>(false);

    const [zoom,setZoom] = useState(10);
    const map = useMap();

    const toggleHideResult = useCallback(()=>{
        setHideResult(!hideResult);
    },[hideResult]);

    const selectDealer = useCallback((dealer:DealerSearchData|null)=>{
        setSelectedDealer(dealer);
        if(dealer!=null){
            setPosition({
                lat:parseFloat(dealer.lat),
                lng:parseFloat(dealer.lng)
            })
            setZoom(15)
            setHideResult(false)
        }
    },[]);

    const searchLocation = useCallback((result:GeolocationResult)=>{
        setSearchPosition(result.coords);
        setPosition(result.coords);
        setSearchOption({
            query:result.query,
            type:result.type
        })
    },[]);

    // Set init position
    useEffect(() => {
        const search = (position:Position)=>{
            setSearchPosition(position);
            setPosition(position);
        }
        const setDefaultPosition = (error:any)=>{
            const {default_position} = (window as any).rz_store_locator;
            setPosition({
                lat:default_position[0],
                lng:default_position[1]
            });

        }
        const setUserPosition = (position:any) =>{
            const { latitude, longitude } = position.coords;
            search({
                lat:latitude,
                lng:longitude
            })
        }
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(setUserPosition, setDefaultPosition,{
                timeout:10000
            });
        }
    }, []);

    // Search dealer
    useEffect(() => {
        console.log("SEARCH",searchPosition,map,searchOption);
        if(searchPosition==null || map==null || searchOption == null)
            return;
        console.log("SEARCH");
        const findNearDealer = async (position:Position)=>{
            setSearching(true);
            try{
                const result = await DealerService.search(position,searchOption?.type,searchOption?.query);
                setDealers(result);
                const zoom = findZoomLevel(map,result);
                if(zoom) {
                    setZoom(zoom);
                }

            }
            catch(e){

            }
            setSearching(false);
        }
        findNearDealer(searchPosition);

    },[searchPosition,map,searchOption]);









    return (
        <StoreLocatorContext.Provider value={{
            position: position,
            hideResult:hideResult,
            searchPosition:searchPosition,
            dealers: dealers,
            selectDealer: selectDealer,
            selectedDealer:selectedDealer,
            zoom:zoom,
            searching:searching,
            setZoom:setZoom,
            setPosition:setPosition,
            search:searchLocation,
            toggleHideResult:toggleHideResult
        }}>
            {children}
        </StoreLocatorContext.Provider>);
}

export const useStoreLocatorContext  = ()=> useContext(StoreLocatorContext);


export {StoreLocatorContext, StoreLocatorProvider};
